import SubscriptionsTable from '@/features/subscriptions/components/SubscriptionsTable'
import { useSubscriptions } from '../../../features/accounts/api/get-subscriptions'
import PageLayout from '@/components/ui/page-layout'
import NewSubscriptionAction from '@/features/subscriptions/components/NewSubscriptionAction'
import * as React from 'react'

export default function SubscriptionsRoute() {
    const [accountId, setAccountId] = React.useState<string>('')
    const [scope, setScope] = React.useState<'all' | 'account' | 'store'>('all')
    const [renewalRange, setRenewalRange] = React.useState<{ from: Date | null; to: Date | null }>({ from: null, to: null })
    const [search, setSearch] = React.useState<string>('')
    const [billing, setBilling] = React.useState<'all' | 'direct' | 'fastbound'>('all')
    const debouncedSearch = React.useMemo(() => {
        let timeout: any
        return {
            get value() { return undefined as unknown as string },
            subscribe: (cb: (v: string) => void) => {
                // noop placeholder to satisfy types
                return () => { }
            }
        }
    }, [])

    const [debouncedSearchValue, setDebouncedSearchValue] = React.useState<string>('')
    React.useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchValue(search), 400)
        return () => clearTimeout(t)
    }, [search])

    const [pageIndex, setPageIndex] = React.useState(0) // 0-based
    const [pageSize, setPageSize] = React.useState(25)
    const [sorting, setSorting] = React.useState<Array<{ id: string; desc: boolean }>>([])

    const sortString = React.useMemo(() => {
        if (!sorting || sorting.length === 0) return ''
        const first = sorting[0]
        if (!first?.id) return ''
        return `${first.desc ? '-' : ''}${first.id}`
    }, [sorting])

    const subscriptions = useSubscriptions({
        params: {
            accountId: accountId || undefined,
            billing: billing || undefined,
            renewalAt: {
                from: renewalRange.from || undefined,
                to: renewalRange.to || undefined,
            },
            commonQuery: {
                page: pageIndex + 1,
                size: pageSize,
                sort: sortString || undefined,
                search: debouncedSearchValue || undefined,
                includeTotals: true,
            },
        },
    })

    const pageCount = subscriptions.data?.totalPages || Math.max(1, Math.ceil((subscriptions.data?.totalItems || 0) / (pageSize || 1)))

    const handlePaginationChange = (updater: any) => {
        const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater
        const nextPageIndex = next.pageIndex ?? pageIndex
        const nextPageSize = next.pageSize ?? pageSize
        setPageIndex(nextPageIndex)
        setPageSize(nextPageSize)
    }

    const handleSortingChange = (updater: any) => {
        const next = typeof updater === 'function' ? updater(sorting) : updater
        setSorting(next || [])
        setPageIndex(0) // reset to first page on sort change
    }

    return (
        <PageLayout
            title='Subscriptions'
            subtitle='Manage subscriptions'
            action={<NewSubscriptionAction />}
        >
            <SubscriptionsTable
                data={subscriptions.data?.items || []}
                isLoading={subscriptions.isLoading}
                pageIndex={pageIndex}
                pageSize={pageSize}
                pageCount={pageCount}
                onPaginationChange={handlePaginationChange}
                sorting={sorting}
                onSortingChange={handleSortingChange}
                accountId={accountId}
                onAccountChange={(v) => { setAccountId(v); setPageIndex(0) }}
                scope={scope}
                onScopeChange={(v) => { setScope(v); setPageIndex(0) }}
                renewalRange={renewalRange}
                onRenewalRangeChange={(v) => { setRenewalRange(v); setPageIndex(0) }}
                search={search}
                onSearchChange={(v) => { setSearch(v); setPageIndex(0) }}
                billing={billing}
                onBillingChange={(v) => { setBilling(v); setPageIndex(0) }}
                totals={subscriptions.data?.totals}
                totalItems={subscriptions.data?.totalItems}
            />
        </PageLayout>
    )
}


